import * as React from 'react';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useSearchParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/ui/BackButton';
import { useToast } from '@/components/ui/use-toast';
import { ErrorDisplay } from '@/components/ui/error-display';
import { CreateUpsellInput, useCreateUpsell } from '@/lib/api/upsells';
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils';
import { UpsellForm } from '@/components/form/upsell/upsell-form';
import { upsellFormSchema, type UpsellFormData } from '@/components/form/upsell/validation';
import { useStoreNavigate } from '@/hooks/use-app-navigate';

/**
 * Page for creating a new upsell
 */
const CreateUpsellPageInner = () => {
    const { t } = useTranslation('upsells');
    const [searchParams] = useSearchParams();
    const typeParam = searchParams.get('type') as 'orderbump' | 'upsell' | null;
    const { id: storeId, navigate, storeRoutes } = useStoreNavigate();
    const { toast } = useToast();
    const createUpsellMutation = useCreateUpsell();
    const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null);

    const methods = useForm<any>({
        defaultValues: {
            offerTitle: '',
            type: typeParam === 'upsell' ? 'upsell' : 'orderbump',
            enabled: true,
            triggerType: 'any',
            triggers: [{ productId: null, type: 'any' as const }],
            offers: [],
            orderBumpOffers: [],
        },
        resolver: zodResolver(upsellFormSchema) as any,
        mode: 'onBlur',
    });
    const {
        handleSubmit,
        formState: { isSubmitting },
    } = methods;

    const handleCreateUpsell = async (data: any) => {
        // Type assertion after validation
        const validatedData = data as UpsellFormData;
        try {
            setFormError(null);

            const input: CreateUpsellInput = {
                storeId,
                offerTitle: validatedData.offerTitle,
                enabled: validatedData.enabled,
                type: validatedData.type,
                triggers: validatedData.triggers.map((trigger: any) => ({
                    productId: trigger.productId,
                    type: trigger.type || 'product',
                })),
                // Only send the relevant offers array based on type
                offers:
                    validatedData.type === 'upsell'
                        ? validatedData.offers.map((offer: any) => ({
                            productId: offer.productId,
                            variantId: offer.variantId,
                            priceId: offer.priceId,
                            title: offer.title || '',
                            titleTrans: offer.titleTrans || { en: '' },
                        }))
                        : [],
                orderBumpOffers:
                    validatedData.type === 'orderbump'
                        ? validatedData.orderBumpOffers.map((offer: any) => ({
                            productId: offer.productId,
                            variantId: offer.variantId,
                            priceId: offer.priceId,
                            type: offer.type,
                            precheck: offer.precheck,
                            quantity: offer.quantity,
                            titleTrans: offer.titleTrans,
                            descriptionTrans: offer.descriptionTrans,
                            overrideImageUrl: offer.overrideImageUrl,
                            displayPrice: offer.displayPrice,
                            displayCompareAtPrice: offer.displayCompareAtPrice,
                            compareAtPriceDiscount: offer.compareAtPriceDiscount,
                            rules: offer.rules || [],
                        }))
                        : [],
            };

            await createUpsellMutation.mutateAsync(input);

            // Wait longer for cache invalidation to complete and propagate
            setTimeout(() => {
                navigate(storeRoutes.UPSELLS());
            }, 500);
        } catch (error) {
            const parsedError = parseTRPCError(error);
            setFormError(parsedError);

            if (!parsedError.isValidationError) {
                toast({
                    title: t('error.error'),
                    description: parsedError.message,
                    variant: 'destructive',
                });
            }
        }
    };

    return (
        <div className="bg-background min-h-screen">
            <div className="flex flex-col space-y-4 sm:space-y-6">
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(handleCreateUpsell)}>
                        <div className="mb-4 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <BackButton to={storeRoutes.UPSELLS()} />
                                <div>
                                    <h1 className="text-2xl font-semibold">{t('create.title')}</h1>
                                    <p className="text-muted-foreground text-sm">{t('create.description')}</p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => navigate(storeRoutes.UPSELLS())}
                                    disabled={isSubmitting}
                                >
                                    {t('actions.cancel')}
                                </Button>
                                <Button type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? t('actions.creating') : t('actions.create')}
                                </Button>
                            </div>
                        </div>

                        {formError && !formError.isValidationError && (
                            <div className="mb-6">
                                <ErrorDisplay error={formError} />
                            </div>
                        )}

                        <UpsellForm />
                    </form>
                </FormProvider>
            </div>
        </div>
    );
};

export const CreateUpsellPage = withRequiredPermission(CreateUpsellPageInner, PERMISSIONS.STORES_MANAGE);
export default CreateUpsellPage;
